import React, { useState, useMemo } from 'react';
import { Search, Phone, MapPin, X, ChevronDown, ChevronUp, Package } from "lucide-react";

const ITEM_COLORS = {
  "LPG 20kg":   { bg: "#e8f0fb", color: "#185FA5" },
  "LPG 50kg":   { bg: "#ede9fe", color: "#7C3AED" },
  "산소(O2)":   { bg: "#e8faf2", color: "#059669" },
  "질소(N2)":   { bg: "#fff7ed", color: "#D97706" },
  "아르곤(Ar)": { bg: "#fef2f2", color: "#DC2626" },
  "탄산(CO2)":  { bg: "#f0fdf4", color: "#16A34A" },
};

const CustomerList = ({ customers = [], deliveries = [], collections = [], cylinders = [] }) => {
  const [query, setQuery]         = useState("");
  const [expandedId, setExpandedId] = useState(null);

  // 거래처별 상세 통계
  const customerStats = useMemo(() => {
    const stats = {};
    customers.forEach(c => {
      // 현재 거래처에 출고 중인 용기 (품목별) — 실물 추적
      const out = cylinders.filter(cyl => cyl.locType === 'customer' && cyl.location === c.name);
      const byItem = {};
      out.forEach(cyl => {
        byItem[cyl.item] = (byItem[cyl.item] || 0) + 1;
      });

      // 순재고 = 배송 수량 합계 - 회수 수량 합계 (품목별, 음수 가능)
      const netByItem = {};
      deliveries.filter(d => d.customer === c.name).forEach(d => {
        if (d.items) {
          Object.entries(d.items).forEach(([item, qty]) => {
            netByItem[item] = (netByItem[item] || 0) + qty;
          });
        } else if (d.item) {
          netByItem[d.item] = (netByItem[d.item] || 0) + (d.quantity || 0);
        }
      });
      collections.filter(col => col.customer === c.name).forEach(col => {
        if (col.items) {
          Object.entries(col.items).forEach(([rawItem, qty]) => {
            // 회수 품목은 "LPG 20kg (공병)" 형태일 수 있어 정규화
            const item = rawItem.replace(/\s*\(공병\)$/, '');
            netByItem[item] = (netByItem[item] || 0) - qty;
          });
        } else if (col.item) {
          const item = col.item.replace(/\s*\(공병\)$/, '');
          netByItem[item] = (netByItem[item] || 0) - (col.quantity || 0);
        }
      });
      const totalNet = Object.values(netByItem).reduce((s, v) => s + v, 0);

      // 배송 누적 금액
      const totalSales = deliveries
        .filter(d => d.customer === c.name)
        .reduce((s, d) => s + (d.amount || 0), 0);

      // 최근 배송일
      const customerDeliveries = deliveries.filter(d => d.customer === c.name);
      const lastDelivery = customerDeliveries.length > 0
        ? customerDeliveries[customerDeliveries.length - 1].date
        : null;

      stats[c.name] = { byItem, totalOut: out.length, netByItem, totalNet, totalSales, lastDelivery, deliveryCount: customerDeliveries.length };
    });
    return stats;
  }, [customers, deliveries, collections, cylinders]);

  const filtered = useMemo(() => {
    const q = query.trim();
    if (!q) return customers;
    return customers.filter(c => {
      const phones = c.phones ?? (c.phone ? [c.phone] : []);
      return c.name.includes(q) || phones.some(p => p.includes(q)) || (c.address || '').includes(q);
    });
  }, [customers, query]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

      {/* 검색창 */}
      <div style={searchWrap}>
        <Search size={16} color="#aaa" />
        <input style={searchInput} placeholder="거래처명, 연락처, 주소로 검색..."
          value={query} onChange={e => setQuery(e.target.value)} autoFocus />
        {query && <button onClick={() => setQuery("")} style={clearBtn}><X size={14} /></button>}
      </div>

      {/* 요약 배너 */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <div style={statChip}>
          거래처 <strong style={{ color: '#185FA5' }}>{customers.length}</strong>곳
        </div>
        <div style={statChip}>
          출고 중인 용기 <strong style={{ color: '#DC2626' }}>
            {cylinders.filter(c => c.locType === 'customer').length}
          </strong>개
        </div>
        <div style={statChip}>
          누적 배송 <strong style={{ color: '#059669' }}>{deliveries.length}</strong>건
        </div>
      </div>

      {/* 거래처 카드 목록 */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {filtered.map(c => {
          const s = customerStats[c.name] || { byItem: {}, totalOut: 0, netByItem: {}, totalNet: 0, totalSales: 0, lastDelivery: null, deliveryCount: 0 };
          const isExpanded = expandedId === c.id;
          const hasNet = s.totalNet !== 0;
          const isNegative = s.totalNet < 0;

          return (
            <div key={c.id} style={{ background: 'white', borderRadius: 12, border: `1px solid ${isNegative ? '#fde68a' : hasNet ? '#fecaca' : '#d3d1c7'}`, overflow: 'hidden' }}>

              {/* 메인 행 */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '16px 20px', flexWrap: 'wrap' }}>

                {/* 거래처명 + 연락처 */}
                <div style={{ flex: '1 1 180px' }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: '#1e293b', marginBottom: 3 }}>
                    {highlight(c.name, query)}
                  </div>
                  {(c.phones ?? (c.phone ? [c.phone] : [])).map((p, i) => (
                    <div key={i} style={{ fontSize: 13, color: '#777', display: 'flex', alignItems: 'center', gap: 4 }}>
                      <Phone size={11} /> {highlight(p, query)}
                    </div>
                  ))}
                  {!(c.phones ?? (c.phone ? [c.phone] : [])).length && (
                    <div style={{ fontSize: 13, color: '#ccc' }}>연락처 없음</div>
                  )}
                </div>

                {/* 주소 */}
                <div style={{ flex: '2 1 200px', fontSize: 13, color: '#888', display: 'flex', alignItems: 'center', gap: 4 }}>
                  <MapPin size={11} /> {highlight(c.address, query)}
                </div>

                {/* 순재고 (배송 - 회수, 음수 가능) */}
                <div style={{ flex: '1 1 160px', textAlign: 'right' }}>
                  <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>순재고 (배송-회수)</div>
                  {Object.keys(s.netByItem).length > 0 ? (
                    <>
                      <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', justifyContent: 'flex-end', marginBottom: 4 }}>
                        {Object.entries(s.netByItem).map(([item, net]) => {
                          const col = ITEM_COLORS[item] || { bg: '#f5f5f5', color: '#555' };
                          const negCol = { bg: '#fef3c7', color: '#92400E' };
                          const c2 = net < 0 ? negCol : col;
                          return (
                            <span key={item} style={{ fontSize: 12, fontWeight: 700, padding: '3px 8px', borderRadius: 10, background: c2.bg, color: c2.color }}>
                              {item} {net > 0 ? '+' : ''}{net}본
                            </span>
                          );
                        })}
                      </div>
                      <div style={{ fontSize: 13, fontWeight: 800, color: isNegative ? '#D97706' : s.totalNet > 0 ? '#DC2626' : '#059669', marginTop: 2 }}>
                        {isNegative ? '▼ ' : s.totalNet > 0 ? '▲ ' : ''}총 {s.totalNet}개
                        {isNegative && <span style={{ fontSize: 11, fontWeight: 400, marginLeft: 4 }}>(음수재고)</span>}
                      </div>
                    </>
                  ) : (
                    <span style={{ fontSize: 13, color: '#ccc' }}>없음</span>
                  )}
                </div>

                {/* 배송 통계 */}
                <div style={{ flex: '0 0 120px', textAlign: 'right' }}>
                  <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>배송 누적</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: s.totalSales > 0 ? '#185FA5' : '#ccc' }}>
                    {s.totalSales > 0 ? `${s.totalSales.toLocaleString()}원` : '—'}
                  </div>
                  {s.lastDelivery && (
                    <div style={{ fontSize: 11, color: '#aaa', marginTop: 2 }}>최근 {s.lastDelivery}</div>
                  )}
                </div>

                {/* 펼치기 버튼 */}
                <button onClick={() => setExpandedId(isExpanded ? null : c.id)}
                  style={{ background: 'none', border: '1px solid #e5e3db', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', color: '#888', display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
                  {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                  상세
                </button>
              </div>

              {/* 펼쳐진 상세 */}
              {isExpanded && (
                <div style={{ borderTop: '1px solid #f0f0f0', padding: '16px 20px', background: '#fafafa' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>

                    {/* 출고 중인 용기 목록 */}
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 700, color: '#555', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                        <Package size={13} color="#DC2626" /> 출고 중인 용기 (시스템 추적)
                      </div>
                      {s.totalOut === 0 ? (
                        <div style={{ fontSize: 13, color: '#ccc', marginBottom: 10 }}>추적된 출고 용기 없음</div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 10 }}>
                          {cylinders
                            .filter(cyl => cyl.locType === 'customer' && cyl.location === c.name)
                            .map(cyl => {
                              const col = ITEM_COLORS[cyl.item] || { bg: '#f5f5f5', color: '#555' };
                              return (
                                <div key={cyl.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                                  <span style={{ fontFamily: 'monospace', fontWeight: 700, color: '#333' }}>{cyl.id}</span>
                                  <span style={{ fontSize: 11, background: col.bg, color: col.color, padding: '1px 7px', borderRadius: 8, fontWeight: 600 }}>{cyl.item}</span>
                                </div>
                              );
                            })
                          }
                        </div>
                      )}
                      {/* 순재고 요약 */}
                      {Object.keys(s.netByItem).length > 0 && (
                        <div style={{ background: s.totalNet < 0 ? '#fef3c7' : '#f0f4ff', borderRadius: 8, padding: '8px 12px', border: `1px solid ${s.totalNet < 0 ? '#fde68a' : '#c7d9f5'}` }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color: s.totalNet < 0 ? '#92400E' : '#185FA5', marginBottom: 5 }}>
                            순재고 (배송 - 회수)
                          </div>
                          {Object.entries(s.netByItem).map(([item, net]) => (
                            <div key={item} style={{ fontSize: 12, display: 'flex', justifyContent: 'space-between' }}>
                              <span style={{ color: '#555' }}>{item}</span>
                              <span style={{ fontWeight: 700, color: net < 0 ? '#D97706' : net > 0 ? '#DC2626' : '#059669' }}>
                                {net > 0 ? '+' : ''}{net}본
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* 최근 배송 이력 */}
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 700, color: '#555', marginBottom: 10 }}>최근 배송 이력</div>
                      {s.deliveryCount === 0 ? (
                        <div style={{ fontSize: 13, color: '#ccc' }}>배송 이력 없음</div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          {deliveries
                            .filter(d => d.customer === c.name)
                            .slice(-5)
                            .reverse()
                            .map(d => (
                              <div key={d.id} style={{ fontSize: 13, display: 'flex', gap: 10, alignItems: 'center' }}>
                                <span style={{ color: '#999', minWidth: 80 }}>{d.date}</span>
                                <span style={{ color: '#555' }}>
                                  {d.items
                                    ? Object.entries(d.items).map(([item, cnt]) => `${item} ${cnt}본`).join(', ')
                                    : `${d.item} ${d.quantity}본`}
                                </span>
                                <span style={{ marginLeft: 'auto', fontWeight: 700, color: '#185FA5' }}>
                                  {(d.amount || 0).toLocaleString()}원
                                </span>
                              </div>
                            ))
                          }
                          {s.deliveryCount > 5 && (
                            <div style={{ fontSize: 12, color: '#aaa' }}>외 {s.deliveryCount - 5}건 더...</div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {filtered.length === 0 && (
          <div style={{ padding: 40, textAlign: "center", color: "#aaa", fontSize: 14, background: 'white', borderRadius: 12, border: '1px solid #d3d1c7' }}>
            "{query}"에 해당하는 거래처가 없습니다
          </div>
        )}
      </div>
    </div>
  );
};

const highlight = (text, query) => {
  if (!query.trim()) return text;
  const idx = text.indexOf(query);
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark style={{ background: "#fef08a", borderRadius: 2, padding: "0 1px" }}>{text.slice(idx, idx + query.length)}</mark>
      {text.slice(idx + query.length)}
    </>
  );
};

const searchWrap = { display: "flex", alignItems: "center", gap: 10, background: "white", border: "1.5px solid #d3d1c7", borderRadius: 10, padding: "10px 16px", boxShadow: "0 2px 8px rgba(0,0,0,0.04)" };
const searchInput = { flex: 1, border: "none", outline: "none", fontSize: 15, background: "transparent" };
const clearBtn   = { background: "none", border: "none", cursor: "pointer", color: "#aaa", display: "flex", padding: 2, borderRadius: 4 };
const statChip   = { fontSize: 13, color: '#555', background: 'white', border: '1px solid #d3d1c7', borderRadius: 20, padding: '6px 14px' };

export default CustomerList;
